import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import {
  GraduationCap, MapPin, Briefcase, Flower, Heart, HandsPraying, SealCheck, ChatsCircle,
  Clock, PencilSimple, CircleNotch, UserCircle, Buildings, WhatsappLogo, Sparkle, Trophy, UsersThree,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import SiteDiscussion from "@/components/SiteDiscussion";
import SuggestCorrection from "@/components/SuggestCorrection";
import PhotoArchive from "@/components/PhotoArchive";
import ShareButton from "@/components/ShareButton";
import { EDITION } from "@/lib/edition";

const THEME = { surface: "#ffffff", text: "#1a2b25", primary: "#1f3d33" };
const STATUS_BADGE = {
  active: { label: "Active", cls: "border-green-200 bg-green-50 text-green-800" },
  retired: { label: "Retired", cls: "border-blue-200 bg-blue-50 text-blue-800" },
  former: { label: "Former Staff", cls: "border-slate-200 bg-slate-50 text-slate-700" },
  deceased: { label: "In Memoriam", cls: "border-primary/20 bg-primary/5 text-primary" },
  unknown: { label: "Status Unknown", cls: "border-slate-200 bg-slate-50 text-slate-500" },
};

export default function PersonProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [tributes, setTributes] = useState([]);
  const [students, setStudents] = useState([]);
  const [tab, setTab] = useState("profile");

  const load = useCallback(() => {
    api.get(`/people/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
    api.get(`/people/${id}/tributes`).then((r) => setTributes(r.data || [])).catch(() => {});
    api.get(`/people/${id}/students`).then((r) => setStudents(r.data.students || [])).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useSeo(p && p !== false ? p.name : "Person", p && p !== false ? (p.title || p.bio) : null, p && p !== false ? p.photo_url : null);

  if (p === null) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;
  if (p === false) return <div className="grid min-h-screen place-items-center text-muted-foreground">Profile not found.</div>;

  const deceased = p.status === "deceased";
  const sb = STATUS_BADGE[p.status] || STATUS_BADGE.unknown;
  const emp = p.employments || [];
  const actionCount = (a) => tributes.filter((t) => t.action === a).length;
  const legacyUrl = `${EDITION.url}/person/${id}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`I honoured ${p.name} on ${EDITION.brand} — a teacher who shaped lives. See their teaching legacy and add your thanks: ${legacyUrl}`)}`;
  const st = p.stats || {};

  return (
    <div className="min-h-screen bg-[#faf8f3]" data-testid="person-profile">
      {/* Memorial banner */}
      {deceased && (
        <div className="bg-primary py-10 text-center text-primary-foreground" data-testid="memorial-banner">
          <p className="text-sm uppercase tracking-widest opacity-80">In Loving Memory of</p>
          <h1 className="mt-1 font-heading text-4xl font-light">{p.name}</h1>
          <p className="mt-1 opacity-90">{[p.birth_year, p.death_year].filter(Boolean).join(" – ")}</p>
          <p className="mt-2 text-sm italic opacity-80">{p.memorial?.epitaph || "Forever remembered by the students and communities whose lives they touched."}</p>
        </div>
      )}

      <div className="mx-auto max-w-4xl px-5 py-8">
        {/* header card */}
        <div className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-white p-6 shadow-sm sm:flex-row sm:items-start">
          <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-green-50 to-orange-50">
            {p.photo_url ? <img src={p.photo_url.startsWith("http") ? p.photo_url : fileUrl(p.photo_url)} alt={p.name} className="h-full w-full object-cover" /> : <UserCircle size={54} weight="duotone" className="text-green-600" />}
          </div>
          <div className="min-w-0 flex-1">
            {!deceased && <h1 className="font-heading text-3xl font-medium">{p.name}</h1>}
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {p.title && <span className="flex items-center gap-1"><Briefcase size={14} /> {p.title}</span>}
              <Badge variant="outline" className={`rounded-full font-normal ${sb.cls}`} data-testid="person-status-badge">{sb.label}</Badge>
              {p.subcategory && <Badge variant="outline" className="rounded-full font-normal">{p.subcategory}</Badge>}
              {(p.verification_status === "self_verified" || p.verification_status === "family_verified") && (
                <span className="flex items-center gap-1 text-primary"><SealCheck size={14} weight="fill" /> {p.verification_status === "family_verified" ? "Family Verified" : "Verified"}</span>
              )}
            </div>
            {emp.length > 0 && (
              <p className="mt-2 text-sm text-muted-foreground">
                {emp.length} institution{emp.length > 1 ? "s" : ""} · {emp.map((e) => e.institution_name).slice(0, 3).join(", ")}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {!deceased && (
                <Button size="sm" className="gap-1.5 rounded-full" data-testid="thank-teacher-cta"
                  onClick={() => { setTab("thanks"); setTimeout(() => document.getElementById("person-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60); }}>
                  <GraduationCap size={15} weight="fill" /> Thank this teacher
                </Button>
              )}
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="whatsapp-share-teacher">
                <Button size="sm" variant="outline" className="gap-1.5 rounded-full border-[#25D366]/40 text-[#128C4A] hover:bg-[#25D366]/10"><WhatsappLogo size={15} weight="fill" /> Share on WhatsApp</Button>
              </a>
              {p.can_edit && <ManageStatusDialog person={p} onDone={load} />}
              {user && <TaughtMeDialog personId={id} personName={p.name} defaultInstitution={emp[0]?.institution_name || ""} defaultInstId={emp[0]?.institution_id || ""} onDone={load} />}
              {!p.can_edit && user && p.claim_status !== "verified" && <ClaimDialog personId={id} personName={p.name} onDone={load} />}
              <ShareButton entityType="person" entityId={id} targetPath={`/person/${id}`} title={p.name} label="Share / QR" />
              <SuggestCorrection entityType="person" entityId={id} entityName={p.name} variant="button" />
            </div>
          </div>
        </div>

        {/* Recognition stats — a teacher's living legacy */}
        <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-testid="teacher-stats">
          {[
            { icon: Clock, val: st.years_teaching ?? 0, label: "Years teaching" },
            { icon: UsersThree, val: st.alumni_connected ?? 0, label: "Alumni connected" },
            { icon: GraduationCap, val: st.thank_you_count ?? 0, label: "Thank-yous" },
            { icon: Trophy, val: st.social_capital ?? 0, label: "Social Capital" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-border/60 bg-white p-4 text-center shadow-sm" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <s.icon size={20} weight="duotone" className="mx-auto text-primary" />
              <p className="mt-1 font-heading text-2xl font-semibold leading-none">{s.val}</p>
              <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        {/* virtual actions for memorials */}
        {deceased && (
          <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="memorial-actions">
            {[{ a: "flower", icon: <Flower size={16} weight="fill" />, label: "Leave a Flower" },
              { a: "candle", icon: <span>🕯️</span>, label: "Light a Candle" },
              { a: "respect", icon: <HandsPraying size={16} weight="fill" />, label: "Pay Respect" }].map((x) => (
              <MemorialActionButton key={x.a} personId={id} action={x.a} icon={x.icon} label={x.label} count={actionCount(x.a)} disabled={!user} onDone={load} />
            ))}
          </div>
        )}

        {/* tabs */}
        <Tabs value={tab} onValueChange={setTab} className="mt-6" id="person-tabs">
          <TabsList className="flex-wrap">
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">Students{students.length > 0 ? ` (${students.length})` : ""}</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="memories" data-testid="tab-memories">Memories</TabsTrigger>
            <TabsTrigger value="thanks" data-testid="tab-thanks">{deceased ? "Tributes" : "Thank a Teacher"}</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-5 space-y-5">
            {p.bio && <div className="rounded-2xl border border-border/60 bg-white p-5"><h3 className="mb-2 font-heading text-lg">About</h3><p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{p.bio}</p></div>}
            <div className="rounded-2xl border border-border/60 bg-white p-5">
              <h3 className="mb-3 font-heading text-lg">Employment history</h3>
              {emp.length === 0 ? <p className="text-sm text-muted-foreground">No employment records yet.</p> : (
                <div className="space-y-3">
                  {emp.map((e, i) => (
                    <div key={i} className="flex gap-3 border-l-2 border-green-200 pl-3">
                      <div>
                        <div className="text-sm font-medium">{e.position || "Staff"}{e.department ? ` · ${e.department}` : ""}</div>
                        <Link to={e.institution_id ? `/institutions/${e.institution_id}` : "#"} className="text-sm text-primary hover:underline">{e.institution_name}</Link>
                        <div className="text-xs text-muted-foreground">{[e.start_year, e.end_year || "present"].filter(Boolean).join(" – ")}{e.subjects?.length ? ` · ${e.subjects.join(", ")}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="mt-5">
            <div className="rounded-2xl border border-border/60 bg-white p-5">
              {p.timeline?.length === 0 ? <p className="text-sm text-muted-foreground">No timeline events yet.</p> : (
                <ol className="space-y-3">
                  {p.timeline.map((t, i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`timeline-${i}`}>
                      <span className="mt-0.5 grid h-7 w-12 shrink-0 place-items-center rounded-full bg-green-50 text-xs font-medium text-green-700">{t.year || "—"}</span>
                      <div><span className="text-sm">{t.label}</span>{t.span && <span className="ml-1 text-xs text-muted-foreground">({t.span})</span>}<span className="ml-2 text-[10px] uppercase text-muted-foreground">{t.verified ? "verified" : "community"}</span></div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </TabsContent>

          <TabsContent value="students" className="mt-5">
            <div className="rounded-2xl border border-border/60 bg-white p-5" data-testid="students-tab">
              <h3 className="mb-3 font-heading text-lg">Students who remember {p.name}</h3>
              {students.length === 0 ? <p className="text-sm text-muted-foreground">No one has linked yet. If they taught you, use "This person taught me" above.</p> : (
                <div className="space-y-3">
                  {students.map((s) => (
                    <div key={s.id} className="flex items-center gap-3" data-testid={`student-${s.id}`}>
                      {s.avatar_file_id ? <img src={fileUrl(s.avatar_file_id)} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-xs text-white">{(s.student_name || "?")[0]}</div>}
                      <div><span className="text-sm font-medium">{s.student_name}</span><span className="ml-2 text-xs text-muted-foreground">{[s.subject, s.class_or_course, s.year_or_period].filter(Boolean).join(" · ")}{s.institution_name ? ` — ${s.institution_name}` : ""}</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="photos" className="mt-5">
            <div className="rounded-2xl border border-border/60 bg-white p-5">
              <PhotoArchive scopeType="person" scopeId={id} scopeName={p.name} />
            </div>
          </TabsContent>

          <TabsContent value="memories" className="mt-5">
            <SiteDiscussion boardType="person" boardId={id} listUrl={`/public/person/${id}/discussion`} t={THEME} placeholder="Share a memory of this person…" />
          </TabsContent>

          <TabsContent value="thanks" className="mt-5 space-y-5">
            <ThankForm personId={id} deceased={deceased} defaultInstitution={emp[0]?.institution_name || ""} onDone={load} user={user} />
            {tributes.filter((t) => t.message).length === 0 ? (
              <p className="text-sm text-muted-foreground">No {deceased ? "tributes" : "messages"} yet.</p>
            ) : (
              <div className="space-y-3">
                {tributes.filter((t) => t.message).map((t) => (
                  <div key={t.id} className="rounded-2xl border border-border/60 bg-white p-4" data-testid={`tribute-${t.id}`}>
                    <div className="flex items-center gap-2">
                      {t.avatar_file_id ? <img src={fileUrl(t.avatar_file_id)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="grid h-8 w-8 place-items-center rounded-full bg-primary text-xs text-white">{(t.author_name || "?")[0]}</div>}
                      <div><span className="text-sm font-semibold">{t.author_name}</span>{t.action && <span className="ml-1 text-sm">{{ flower: "🌹", candle: "🕯️", respect: "🙏" }[t.action]}</span>}</div>
                    </div>
                    <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{t.message}</p>
                    {(t.subject || t.year || t.institution) && <p className="mt-1 text-xs text-muted-foreground/80">{[t.subject, t.institution, t.year].filter(Boolean).join(" · ")}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ThankForm({ personId, deceased, defaultInstitution, onDone, user }) {
  const [f, setF] = useState({ subject: "", year: "", institution: defaultInstitution, message: "", action: "" });
  const [busy, setBusy] = useState(false);
  if (!user) return <div className="rounded-2xl border border-border/60 bg-white p-4 text-sm"><Link to="/login" className="font-medium text-primary">Sign in</Link> to {deceased ? "leave a tribute" : "thank this teacher"}.</div>;
  const submit = async (e) => {
    e.preventDefault();
    if (!f.message.trim()) { toast.error("Please write a message"); return; }
    setBusy(true);
    try {
      await api.post(`/people/${personId}/tributes`, { type: deceased ? "tribute" : "thanks", ...f });
      setF({ subject: "", year: "", institution: defaultInstitution, message: "", action: "" });
      toast.success("Thank you — your message is now part of their history.");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-border/60 bg-white p-5" data-testid="thank-form">
      <h3 className="mb-3 font-heading text-lg">{deceased ? "Leave a tribute" : "Thank this teacher"}</h3>
      <div className="grid grid-cols-3 gap-2">
        <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" data-testid="thank-subject" />
        <Input value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} placeholder="Year (e.g. 1978)" data-testid="thank-year" />
        <Input value={f.institution} onChange={(e) => setF({ ...f, institution: e.target.value })} placeholder="School/College" />
      </div>
      <Textarea rows={3} className="mt-2" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder={deceased ? "Share a memory or tribute…" : "What did they teach you, and how did they influence your life?"} data-testid="thank-message" />
      <div className="mt-2 flex items-center justify-between">
        <Select value={f.action || "none"} onValueChange={(v) => setF({ ...f, action: v === "none" ? "" : v })}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Add a gesture" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No gesture</SelectItem>
            <SelectItem value="respect">🙏 Respect</SelectItem>
            <SelectItem value="flower">🌹 Flower</SelectItem>
            <SelectItem value="candle">🕯️ Candle</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" disabled={busy} className="rounded-full gap-1.5" data-testid="thank-submit">{busy ? <CircleNotch size={15} className="animate-spin" /> : <GraduationCap size={15} />} {busy ? "Posting…" : "Post"}</Button>
      </div>
    </form>
  );
}

function MemorialActionButton({ personId, action, icon, label, count, disabled, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (disabled) { toast.error("Sign in to leave a gesture"); return; }
    setBusy(true);
    try { await api.post(`/people/${personId}/tributes`, { type: "tribute", action, message: "" }); onDone(); }
    catch { toast.error("Could not record"); } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-white px-4 py-2 text-sm text-primary transition-colors hover:bg-primary/5" data-testid={`memorial-action-${action}`}>
      {icon} {label}{count > 0 && <span className="text-xs text-muted-foreground">{count}</span>}
    </button>
  );
}

function ManageStatusDialog({ person, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ status: person.status, death_year: person.death_year || "", source: "", epitaph: "", obituary: "" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/people/${person.id}/status`, f);
      toast.success("Updated");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="manage-status-open"><PencilSimple size={14} /> Manage status</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="manage-status-dialog">
        <DialogHeader><DialogTitle>Manage status</DialogTitle><DialogDescription>Marking someone as deceased creates an Eternal Memorial and requires a source.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
              <SelectItem value="former">Former Staff</SelectItem>
              <SelectItem value="deceased">Deceased</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
          {f.status === "deceased" && (
            <>
              <div><Label className="text-xs">Year of passing</Label><Input value={f.death_year} onChange={(e) => setF({ ...f, death_year: e.target.value })} placeholder="e.g. 2020" className="mt-1" data-testid="death-year" /></div>
              <div><Label className="text-xs">Source / evidence *</Label><Textarea rows={2} value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="Obituary, institution notice, family confirmation…" className="mt-1" data-testid="death-source" /></div>
              <div><Label className="text-xs">Epitaph (optional)</Label><Input value={f.epitaph} onChange={(e) => setF({ ...f, epitaph: e.target.value })} className="mt-1" /></div>
            </>
          )}
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full" data-testid="status-submit">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaughtMeDialog({ personId, personName, defaultInstitution, defaultInstId, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ subject: "", class_or_course: "", year_or_period: "" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/people/${personId}/taught-me`, {
        institution_id: defaultInstId, institution_name: defaultInstitution, ...f,
      });
      toast.success(`Added — ${personName} is now linked as your teacher.`);
      setOpen(false); setF({ subject: "", class_or_course: "", year_or_period: "" }); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="rounded-full gap-1.5" data-testid="taught-me-open"><GraduationCap size={14} /> This person taught me</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="taught-me-dialog">
        <DialogHeader><DialogTitle>{personName} taught me</DialogTitle><DialogDescription>Link yourself as a former student. This builds a real teacher–student network.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="mt-1" placeholder="e.g. Mathematics" data-testid="taught-subject" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Class / Course</Label><Input value={f.class_or_course} onChange={(e) => setF({ ...f, class_or_course: e.target.value })} className="mt-1" placeholder="e.g. Class 5" data-testid="taught-class" /></div>
            <div><Label className="text-xs">Year / period</Label><Input value={f.year_or_period} onChange={(e) => setF({ ...f, year_or_period: e.target.value })} className="mt-1" placeholder="e.g. 1978" data-testid="taught-year" /></div>
          </div>
          {defaultInstitution && <p className="text-xs text-muted-foreground">At <strong>{defaultInstitution}</strong></p>}
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full" data-testid="taught-submit">{busy ? "Saving…" : "Add me as a student"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ClaimDialog({ personId, personName, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ relationship: "self", note: "" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/people/${personId}/claim`, f); toast.success("Claim submitted for review"); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="rounded-full" data-testid="claim-open">Is this you? Claim profile</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="claim-dialog">
        <DialogHeader><DialogTitle>Claim {personName}</DialogTitle><DialogDescription>Verified claims get management rights. Our team reviews each claim.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Your relationship</Label>
            <Select value={f.relationship} onValueChange={(v) => setF({ ...f, relationship: v })}>
              <SelectTrigger className="mt-1" data-testid="claim-relationship"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["self", "spouse", "son", "daughter", "sibling", "grandchild", "relative", "representative", "institution"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r === "self" ? "This is me" : r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Note (how we can verify)</Label><Textarea rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1" data-testid="claim-note" /></div>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full" data-testid="claim-submit">{busy ? "Submitting…" : "Submit claim"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
